import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../../lib/api";
import { StatusBadge } from "../StatusBadge";
import { BookFormDialog } from "../BookFormDialog";
import { Button } from "../ui/button";

const ED = { ebook: ["Kindle ebook", 4], paperback: ["Paperback", 5], hardcover: ["Hardcover", 6], audiobook: ["Audiobook", 7] };

export default function OverviewTab({ book, version, reload }) {
  const [edit, setEdit] = useState(false);
  const navigate = useNavigate();
  const archive = async () => {
    if (!window.confirm("Move this book to Trash? You can restore it from Trash until it is permanently erased.")) return;
    try { const { data } = await api.delete(`/books/${book.id}`); toast.success(data.note); navigate("/app"); } catch (e) { toast.error(errMsg(e)); }
  };
  const rows = [["Subtitle", book.subtitle], ["Author", book.author], ["Publisher", book.publisher], ["Series", book.series_name], ["Book number", book.book_number], ["Genre", book.genre], ["Language", book.language], ["Created", fmtDate(book.created_at)], ["Last activity", book.last_activity]];
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="panel p-6 lg:col-span-2" data-testid="book-metadata">
        <div className="flex justify-between mb-6"><p className="eyebrow">Book details</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-slate-700" onClick={() => setEdit(true)} data-testid="edit-book-button"><Pencil size={14} className="mr-1" />Edit</Button>
            <Button size="sm" variant="outline" className="border-rose-800 text-rose-300" onClick={archive} data-testid="archive-book-button"><Trash2 size={14} className="mr-1" />Move to Trash</Button>
          </div>
        </div>
        <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
          {rows.map(([k, v]) => <div key={k}><dt className="text-xs text-slate-500">{k}</dt><dd className="text-slate-200">{v ?? "—"}</dd></div>)}
        </dl>
        {book.description && <p className="text-sm text-slate-400 mt-6 whitespace-pre-wrap">{book.description}</p>}
      </div>
      <div className="space-y-4">
        <div className="panel p-6">
          <p className="eyebrow mb-4">Manuscript</p>
          <p className="text-sm" data-testid="overview-current">Current: {version ? `v${version.number}` : "none"}</p>
          <p className="text-sm text-slate-400" data-testid="overview-approved">Approved for editions: {book.approved_version_id ? (book.approved_version_id === version?.id ? `v${version.number} (current)` : "an earlier version") : "not yet approved"}</p>
        </div>
        <div className="panel p-6" data-testid="editions-panel">
          <p className="eyebrow mb-4">Editions</p>
          <div className="space-y-3">
            {Object.entries(ED).map(([k, [label, phase]]) => {
              const e = book.editions?.[k] || {};
              return (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span>{label}</span>
                  <span className="flex items-center gap-2"><span className="text-[10px] text-slate-600 font-mono">PHASE {phase}</span><StatusBadge status={e.outdated ? "outdated" : e.status || "not_started"} testId={`edition-status-${k}`} /></span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <BookFormDialog open={edit} onOpenChange={setEdit} book={book} onSaved={reload} />
    </div>
  );
}
